import { refreshToken } from "../services/auth.service";
import storage from "./storage";

/**
 * Extends the global `Window` interface to include the `grecaptcha` property.
 * 
 * @interface Window
 */
declare global {
    interface Window {
        grecaptcha: any;
    }
}

export const getToken = () => {

}

/**
 * 
 * @param {string} SITE_KEY
 * @returns {string} token
 */
export const getCaptchaToken = async (SITE_KEY: string): Promise<any> => {
    let token = null
    await window.grecaptcha?.execute(SITE_KEY, { action: "submit" })
        .then((res: string) => {
            token = res;
        })
    return token;
}

/**
 * Asynchronously refreshes the user's authentication token.
 *
 * @async
 * @function refreshTokenAPI
 * @returns {Promise<boolean>} A promise that resolves to true if the token is refreshed successfully, and false otherwise.
 *
 * @throws {Error} If there's an issue with the token refresh process.
 *
 * @example
 */
export const refreshTokenAPI = async () => {
    if (storage.getToken()) {
        const token = storage.getRefreshToken();
        if(token) {
            await refreshToken({ refresh_token: token }).then((response: any) => {
                storage.setToken(response?.data?.token);
                storage.setRefreshToken(response?.data?.refresh_token);
                return true;
            }).catch((error: any) => {
                return false;
            });
        }
    }
}

export const setApplicationModule = (path: string) => {
    if(path) {
        const prevModule = getApplicationModule();
        const module = path.split('/');
        let currentModule: string = '';
        if(['settings', 'user-management'].includes(module[2])) {
            currentModule = module[3];
        } else {
            currentModule = module[2];
        }
        
        if(prevModule !== currentModule) {
            storage.setData('currentModule', currentModule);
            storage.clearData('filters');
        }
    }
}

export const getApplicationModule = () => {
    return storage.getData('currentModule');
}

export const hasPermission = (permission: string) => {
    const label = storage.getData('label');
    const permissions = storage.getData('permissions');
    return label.includes('SUPER_ADMIN') || permissions.includes(permission);
}

export const setFilter = (key: string, data: any) => {
    const filters = getAppliedFilters(key);
    
    const setFilters: any = {};
    if(filters) {
        setFilters[key] = {...filters, ...data}
    } else {
        setFilters[key] = {...data};
    }
    storage.setData('filters', setFilters);
}

export const getAppliedFilters = (key: any) => {
    const filters = storage.getData('filters');
    if(filters) {
        return filters[key];
    } else {
        return null;
    }
}

export const setLastFocusedField = (module: string, field: 'scan' | 'search') => {
    const current = storage.getData('lastFocusedFields') || {};
    storage.setData('lastFocusedFields', { ...current, [module]: field });
};

export const getLastFocusedField = (module: string): 'scan' | 'search' | undefined => {
    const data = storage.getData('lastFocusedFields');
    return data?.[module];
};

//Filter the Countries and set US and CA in top of the Dropdown
export const formatCountries = (countries: any[]) => {
    return countries
        ?.map(({ name, id, iso2, iso3 }) => ({ label: name, value: id, iso2, iso3 }))
        ?.sort((a, b) => {
            if (a.label === 'United States') return -1;
            if (b.label === 'United States') return 1;
            if (a.label === 'Canada') return -1;
            if (b.label === 'Canada') return 1;

            return a.label.localeCompare(b.label);
        });
};

export const capitalize = (data: string) => {
    return data.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
}

// Function to truncate text
export const truncateText = (text: string, maxLength: number): string => {
    return text?.length > maxLength ? text?.substring(0, maxLength) + "..." : text;
};

/**
 * Formats a cost value to ensure it displays correctly:
 * - If the value has two or fewer decimal places, it shows exactly two decimal places.
 * - If the value has more than two decimal places, it remains unchanged.
 * - Handles dollar sign formatting.
 *
 * @param {string} cost - The cost value to format (including the dollar sign).
 * @returns {string} - The formatted cost as a string.
 */
export const formatCost = (cost: string, maximumFractionDigits: number = 7): string => {

    if(!cost) {
        return '0.00';
    }
    // Convert the cost to a string if it's not already a string
    if (typeof cost !== 'string') {
        cost = String(cost);
    }

    // Determine if the input has a dollar sign
    const hasDollarSign = cost.startsWith('$');

    // Remove the dollar sign and convert to a number
    const num = Number(cost.replace(/^\$/, ''));

    // Check if the conversion to number is successful
    if (isNaN(num)) {
        return '$0.00'; // or return a default value like '$0.00'
    }

    let hasNagative = false;
    let costValue;
    if (num < 0) {
        hasNagative = true;
        costValue = Math?.abs(num);
    } else {
        costValue = parseFloat(num.toString());
    }

    const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: maximumFractionDigits,
    }).format(costValue);

    const amountNumber = formattedAmount.replace(/^\$/, '');

    return `${hasNagative ? '-' : ''}${hasDollarSign ? '$' : ''}${amountNumber}`;
};

export const formatFractionDigits = (num: number, min: number, max: number) => {
    let fixed = num.toFixed(max);
    let actual = parseFloat(fixed);
  
    // Ensure minimum fraction digits
    const fractionLength = actual.toString().split('.')[1]?.length || 0;
    if (fractionLength < min) {
      return parseFloat(actual.toFixed(min));
    }
  
    return actual;
};

export const formatWeightConversion = (weight: number, unit: string) => {
    if(unit === 'pound') {
        const totalOunces = weight * 16; // 1 lb = 16 oz
        const lbs = Math.floor(totalOunces / 16); // Extract the whole pounds
        const oz = Math.round(totalOunces % 16); // Remaining ounces
        return `${lbs} lb. ${oz} oz.`;
    }

    return `${weight} ${unit}`
}

const PENDING_TAB_MIN_DISPLAY_MS = 500;

/**
 * Clones the current document's stylesheets (both linked <link> files and any
 * inline <style> tags, covering both production and dev-server builds) into
 * another same-origin document, so it renders with the app's real Bootstrap/SCSS
 * classes instead of needing its own hand-written CSS.
 */
const clonePageStylesInto = (targetDocument: Document) => {
    document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
        targetDocument.head.appendChild(node.cloneNode(true));
    });
};

/**
 * Opens a blank placeholder tab. Call this BEFORE any awaited work, synchronously
 * within the triggering click handler, so a later same-origin navigation into it
 * isn't treated as a popup once the click's user-activation has expired.
 *
 * @returns {Window | null} the opened window, or null if the browser blocked it outright.
 */
export const openPendingTab = (): Window | null => {
    try {
        const printWindow = window.open('about:blank');
        if (printWindow?.document?.head && printWindow?.document?.body) {
            printWindow.document.title = 'Preparing label…';
            clonePageStylesInto(printWindow.document);
            printWindow.document.body.innerHTML = `
                <div class="theme-loader-z-index align-items-center bg-black bg-opacity-50 d-flex h-100 justify-content-center position-fixed start-0 top-0 w-100 d-block">
                    <div class="spinner-border text-light" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
            `;
        }
        return printWindow;
    } catch {
        return null;
    }
};

/**
 * Resolves a tab opened via openPendingTab() once async work finishes.
 *
 * @param {Window | null} printWindow - the window returned by openPendingTab().
 * @param {boolean} shouldOpen - whether there is anything to show; closes printWindow if false.
 * @param {any} namePayload - object to JSON.stringify into window.name for the destination page to read.
 * @param {string} url - the same-origin URL to navigate printWindow to.
 * @param {number} [openedAt] - timestamp (Date.now()) from when openPendingTab() was called; when provided,
 * the navigation is delayed so the placeholder is visible for at least PENDING_TAB_MIN_DISPLAY_MS.
 * @returns {boolean} false only when shouldOpen was true but printWindow is null (browser fully blocked the pre-await open).
 */
export const resolvePendingTab = (
    printWindow: Window | null,
    shouldOpen: boolean,
    namePayload: any,
    url: string,
    openedAt?: number
): boolean => {
    if (!shouldOpen) {
        printWindow?.close();
        return true;
    }
    if (!printWindow) {
        return false;
    }

    const navigate = () => {
        if (printWindow.closed) return;
        printWindow.name = JSON.stringify(namePayload);
        printWindow.location.href = url;
    };

    const remaining = PENDING_TAB_MIN_DISPLAY_MS - (Date.now() - (openedAt ?? 0));
    if (openedAt && remaining > 0) {
        setTimeout(navigate, remaining);
    } else {
        navigate();
    }

    return true;
};

export const formatBestBefore = (dateStr?: string) => {
    if (!dateStr) return '';
    if (dateStr === '9999-12-31') return 'N/A';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        });
    } catch {
        return dateStr;
    }
};